<script setup>

import { ref, computed, onMounted, onUnmounted } from 'vue';

const { menu } = defineProps({
    menu: Object
});

const emit = defineEmits(['masquer']);

const composantBarreFlottante = ref(null);
const composantsBoutons = ref([]);
const composantBoutonRetour = ref(null);

const itemsCourants = computed(() => {
    if (menu.selectionItems.length > 0) {
        return getItem(menu.selectionItems).items;
    } else {
        return menu.items;
    }
});

const itemsCourantsTries = computed(() => {
    if (itemsCourants.value?.length > 0) {
        const itemsTries = itemsCourants.value.slice();
        itemsTries.sort((item1, item2) => {
            return item1.groupe - item2.groupe;
        });
        return itemsTries;
    } else {
        return [];
    }
});

const itemSelectionne = computed(() => {
    if (menu.selectionItems.length > 0) {
        return getItem(menu.selectionItems);
    } else {
        return null;
    }
});

const positionGauche = computed(() => {
    if (composantBarreFlottante.value) {
        const largeurBarre = composantBarreFlottante.value.getBoundingClientRect().width;
        const positionSouhaitee = menu.composantParent.getBoundingClientRect().left - largeurBarre / 2;
        let largeurViewport = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        return Math.min(Math.max(10, positionSouhaitee), largeurViewport - largeurBarre - 10);
    }
});

const positionHaut = computed(() => {
    return menu.composantParent.getBoundingClientRect().bottom + 20;
});

onMounted(() => {
    window.addEventListener('mousedown', gererClic);
    composantBarreFlottante.value.scrollIntoView({behaviour: "smooth", block: "nearest", inline: "nearest"});
    if (composantsBoutons.value.length > 0) {
        composantsBoutons.value[0].focus();
    }
});

onUnmounted(() => {
    window.removeEventListener('mousedown', gererClic);
});

function gererClic(event) {
    if (composantBarreFlottante.value && event.target !== composantBarreFlottante.value && !composantBarreFlottante.value.contains(event.target)) {
        masquer();
    }
}

function getItem(selection) {
    return getItemRecursive(menu.items, selection);
}

function getItemRecursive(items, selection) {
    if (selection.length === 0) {
        return null;
    } else if (selection.length === 1) {
        return items[selection[0]];
    } else {
        return getItemRecursive(items[selection[0]].items, selection.slice(1));
    }
}

function getStyleBarre() {
    return {
        left: `${positionGauche.value}px`, 
        top: `${positionHaut.value}px`, 
        flexDirection: (menu.affichageVertical ? 'column' : 'row'),
        alignItems: (menu.affichageVertical ? 'stretch' : 'center')
    };    
}

function getStyleBouton(item) {
    const style = {};
    if (menu.affichageVertical) {
        style.marginTop = '2px';
        style.justifyContent = 'flex-start';
    } else {
        style.marginLeft = `${getDecalageBouton(item)}px`;
    }
    if (item.desactive) {
        style.opacity = '50%';
    }
    return style;
}

function getDecalageBouton(item) {
    if (item.groupe > 1) {
        const indiceItem = itemsCourantsTries.value.indexOf(item);
        if (indiceItem > 0) {
            const estPremierItemGroupe = item.groupe > itemsCourantsTries.value[indiceItem - 1].groupe;
            return (estPremierItemGroupe ? 10 : 0);
        } else {
            return 0;
        }
    } else {
        return 0;
    }
}

function getInfobulleBouton(item) {
    if (item.icones && !menu.affichageVertical) {
        var infobulle = `<b>${item.libelle}</b>`;
        if (item.raccourci) {
            infobulle += `<br>Raccourci : ${item.raccourci}`;
        }
        return {value: infobulle, escape: true};
    }
}

function selectionnerItem(item) {
    if (item.commande && !item.desactive) {
        item.commande.apply();
        masquer();
    } else {
        const selectionItems = menu.selectionItems.slice();
        const indiceItem = itemsCourants.value.indexOf(item);
        selectionItems.push(indiceItem);
        afficherItems(selectionItems);
    }
}

function getSelectionSuperieure() {
    if (menu.selectionItems.length > 0) {
        return menu.selectionItems.slice(0, menu.selectionItems.length - 1);
    } else {
        return [];
    }
}

function gererToucheClavier(event) {
    switch(event.key) {
        case 'ArrowUp':
        case 'ArrowLeft': {
            const indiceBouton = composantsBoutons.value.indexOf(event.target);
            selectionnerProchainBoutonActive(indiceBouton, 'GAUCHE');
            break;
        }
        case 'ArrowDown':
        case 'ArrowRight': {
            const indiceBouton = composantsBoutons.value.indexOf(event.target);
            selectionnerProchainBoutonActive(indiceBouton, 'DROITE');
            break;
        }
        case 'Home': {
            const indiceBouton = (composantBoutonRetour.value ? -1 : composantsBoutons.value.length - 1);
            selectionnerProchainBoutonActive(indiceBouton, 'DROITE');
            break;
        }
        case 'End': {
            selectionnerProchainBoutonActive(0, 'GAUCHE');
            break;
        }
        case 'Escape': {
            revenirAuNiveauSuperieur();
            break;
        }
    }
}

function selectionnerProchainBoutonActive(indiceBouton, direction) {
    if (direction === 'DROITE') {
        indiceBouton++;
    } else {
        indiceBouton--;
    }
    if (indiceBouton >= composantsBoutons.value.length) {
        if (composantBoutonRetour.value) {
            indiceBouton = -1;
        } else {
            indiceBouton = 0;
        }
    }
    if (indiceBouton === -1) {
        if (composantBoutonRetour.value) {
            composantBoutonRetour.value.focus();
        } else {
            indiceBouton = composantsBoutons.value.length - 1;
            let composantBouton = composantsBoutons.value[indiceBouton];
            if (!composantBouton.disabled) {
                composantBouton.focus();
            } else {
                selectionnerProchainBoutonActive(indiceBouton, direction);
            }
        }
    } else {
        let composantBouton = composantsBoutons.value[indiceBouton];
        if (!composantBouton.disabled) {
            composantBouton.focus();
        } else {
            selectionnerProchainBoutonActive(indiceBouton, direction);
        }
    }
}

function revenirAuNiveauSuperieur() {
    if (menu.selectionItems.length > 0) {
        afficherItems(getSelectionSuperieure());
    } else {
        masquer();
    }
}

function afficherItems(selectionItems) {
    menu.selectionItems = selectionItems;
}

function masquer() {
    emit('masquer');
    if (menu.quandMasquage) {
        menu.quandMasquage.apply(null);
    } else {
        return menu.composantParent.focus();
    }
}

</script>

<template>
    <div class="barre-flottante"
         ref="composantBarreFlottante"
         :style="getStyleBarre()">
        <div v-if="itemSelectionne" 
            class="item-selectionne"
            v-tooltip.top="`Menu ${itemSelectionne.libelle}`">
            <i v-for="icone in itemSelectionne.icones" :class="icone" />
        </div>
        <label v-else-if="menu.titre" 
            class="titre" >{{ menu.titre }}</label>
        <button v-for="item in itemsCourantsTries"
            class="bouton"
            ref="composantsBoutons"
            :style="[getStyleBouton(item), item.style]"
            :disabled="item.desactive"
            v-tooltip.top="getInfobulleBouton(item)"
            @click.prevent.stop="selectionnerItem(item)"
            @keydown.stop="gererToucheClavier">
            <i v-for="icone in item.icones" 
                :class="icone" 
                :style="{marginRight: menu.affichageVertical ? '5px' : null}" />
            {{ item.icones?.length && !menu.affichageVertical ? '' : item.libelle }}
            <i v-if="!item.commande" class="icone-fleche fa fa-caret-down" />
        </button>
        <button v-if="itemSelectionne"
            ref="composantBoutonRetour"
            class="bouton btn-retour fa fa-rotate-left"
            v-tooltip.top="'Retour'"
            @click.prevent.stop="revenirAuNiveauSuperieur()"
            @keydown.stop="gererToucheClavier" />
    </div>
</template>

<style scoped>

.barre-flottante {
    position: fixed;
    border: 1px solid var(--color-text);
    border-radius: 3px;
    background-color: var(--couleur-fond-zone-expression);
    display: flex;
    align-items: stretch;
    padding: 7px 10px;
	scroll-margin: 50px !important;
    transition: all 1s ease-out;
    z-index: 5;
}

.item-selectionne {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-left: 5px;
    margin-right: 25px;    
    min-width: 40px;
    color: black;
    background-color: var(--couleur-selection);
    border: 1px solid var(--couleur-bordure);
    border-radius: 2px;
}

.titre {
    align-self: center;
    margin-bottom: 10px;
}

.bouton {
    min-width: 40px;
    align-items: center;
}

.icone-fleche {
    margin-left: 5px;
    padding: 0px;
}

.btn-retour {
    margin-left: 30px;
    color: black;
}

</style>